import { Globals } from "@coreapp/CoreApp/Globals";
import { QChartBox } from "@coreapp/CoreApp/QChartBox";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { QXWSMgr } from "@coreapp/CoreApp/QXWSMgr";
import { enumChartElement } from "@coreapp/constants";
import { IsBackgroundFile } from "@coreapp/utils/IsBackgroundFile";
import { getContainerHeight } from "./getContainerSize";

/**
 * @return {Nullable<QXWSMgr>}
 */
export function GetWSMgr() {
  return Globals._wsMgr;
}

export function ResizeApp() {
  if (Globals._app) {
    Globals._app.HandleResize();
  }
}

/**
 * @return {Nullable<QXDoc>}
 */
export function GetDoc() {
  let doc = null;
  let wsMgr = GetWSMgr();
  if (wsMgr) {
    doc = wsMgr.GetDoc();
  }
  return doc;
}

export function GetSubChartMgr() {
  let doc = GetDoc();
  if (!doc) return null;
  return doc.GetSubChartMgr();
}

export function GetBGMgr() {
  const wsMgr = GetWSMgr();
  if (wsMgr) {
    return wsMgr.fBackgroundMgr;
  }
  return null;
}

export function GetUserInfo() {
  let userInfo = null;
  let wsMgr = GetWSMgr();
  if (wsMgr) {
    userInfo = wsMgr.GetUserInfo();
  }
  return userInfo;
}

export function GetState() {
  return Globals._uiConfig.GetUIMode();
}

// Chart box / cell update property method
export const UpdateBoxProperty = (property, value, rerender = true) => {
  let wsMgr = GetWSMgr();
  if (!wsMgr) return;
  wsMgr.UpdateSelectedProp(property, value);
  if (rerender) {
    ReRenderCanvas();
  }
};

export const ReRenderCanvas = () => {
  let wsMgr = GetWSMgr();
  if (!wsMgr) return;
  wsMgr.PrepRenderWS();
  wsMgr.RenderWS();
};

export const GetModalContainerHeight = () => {
  const header = document.querySelector(".appHeader");
  const phoneToolbar = document.querySelector(".ocn-phone-toolbar");
  const baseHeight = getContainerHeight();
  const headerHeight = header ? header.getBoundingClientRect().height : 0;
  const phoneToolbarHeight = phoneToolbar ? phoneToolbar.getBoundingClientRect().height : 0;
  return baseHeight - headerHeight - phoneToolbarHeight;
};

/**
 *
 * @returns {Nullable<QChartBox>}
 */
export const GetCurrentBoxSelection = () => {
  let selection = null;
  let wsMgr = GetWSMgr();
  if (!wsMgr) return selection;
  let doc = GetDoc();
  if (!doc) return selection;

  let firstSelection = wsMgr.GetFirstSelectedElement(null);
  if (firstSelection) {
    let eType = firstSelection.GetElementType();
    if (eType == enumChartElement.chartBox) {
      selection = firstSelection;
    } else if (eType == enumChartElement.boxCell) {
      let pBox = firstSelection.GetParentBox();
      if (pBox.GetElementType() == enumChartElement.chartBox) {
        selection = pBox;
      }
    }
  }
  return selection;
};

export const SetDocDirty = () => {
  let wsMgr = GetWSMgr();
  var dm = wsMgr.GetDocMgr();
  dm.SetDirty(true);
};

export const RefreshChart = requestID => {
  let wsMgr = GetWSMgr();
  let doc = wsMgr.GetDoc();
  if (!doc) return;
  if (doc.IsServerChart()) {
    wsMgr.RefreshBaseFromSource(null, requestID);
  } else {
    wsMgr.RefreshDocFromBase();
  }
};

export const CanEditBackground = () => {
  let inEditMode = Globals._uiConfig.InWorkbenchEditMode();
  let editingBackground = Globals._uiConfig.InBackgroundEditorMode();
  const wsMgr = GetWSMgr();
  const currentFilename = wsMgr?.GetFileName(true);
  const isBackground = currentFilename ? IsBackgroundFile(currentFilename) : false;
  return (inEditMode || editingBackground) && !isBackground;
};

export const UseMetric = () => {
  const wsMgr = GetWSMgr();
  const oi = wsMgr.GetOrgInfo();
  const useMetric = oi.GetUseMetric();
  return useMetric == 1;
};

const GetDefaultSearchKeyContextId = () => {
  const wsMgr = GetWSMgr();
  const org = wsMgr?.GetAccountName();
  const doc = GetDoc();
  const chartID = doc?.GetChartID();
  if (!org || !chartID) {
    return;
  }

  return `${org}_${chartID}`;
};

export const UpdateSessionSearchKeys = (searchCriteria, ctxId = undefined) => {
  ctxId ??= GetDefaultSearchKeyContextId();
  if (!ctxId) {
    return;
  }

  const existingKeys = JSON.parse(localStorage.getItem("userSearchKeys")) ?? {};

  existingKeys[ctxId] = Object.keys(searchCriteria).map(item => ({
    keyDef: item,
    keyName: item,
    isLookup: true
  }));

  localStorage.setItem("userSearchKeys", JSON.stringify(existingKeys));
};

export const GetSessionSearchKeys = (ctxId = undefined) => {
  ctxId ??= GetDefaultSearchKeyContextId();
  if (!ctxId) {
    return;
  }

  const existingKeys = JSON.parse(localStorage.getItem("userSearchKeys"));
  if (!existingKeys) {
    return;
  }

  return existingKeys[ctxId];
};

export const UploadToSystemFolder = () => {
  const wsMgr = GetWSMgr();
  const u = wsMgr.GetUploadMgr();
  const acctName = wsMgr.GetAccountName();
  const theDirectory = "System";
  const user = wsMgr.GetUserName();
  u.DoUpload(acctName, user, theDirectory);
};

export const UpdateChartList = charts => {
  if (!charts) return;

  const wsMgr = GetWSMgr();
  const orgInfo = wsMgr.GetOrgInfo();
  const curDoc = wsMgr.GetDoc();
  const curChartID = curDoc?.GetChartID();
  const isCurrentChartInUpdatedList = charts.some(chart => chart.ID === curChartID);
  if (!isCurrentChartInUpdatedList) {
    wsMgr.CloseDocument();
  }

  const origChartList = orgInfo.GetMasterChartList();
  const newChartList = [];

  for (const chart of origChartList) {
    const chartInAccount = charts.find(c => c.ID === chart.ID);
    // Drop deleted charts from org info
    if (!chartInAccount) {
      continue;
    }

    // Write changes from account settings
    newChartList.push({
      ...chart,
      ...chartInAccount
    });
  }

  // Write new charts to org info
  for (const chart of charts) {
    const chartInOrgInfo = newChartList.find(c => c.ID === chart.ID);
    if (!chartInOrgInfo) {
      newChartList.push(chart);
    }
  }

  orgInfo.SetMasterChartList(newChartList);
  const isMasterChartOpen = curDoc.IsServerChart() && !curDoc.IsPlanChart();
  if (isMasterChartOpen) {
    const currentChartData = orgInfo.GetCurrentBaseChart(curDoc, newChartList);

    if (currentChartData?.name) {
      wsMgr.GetDocMgr().SetFileName(currentChartData.name);
    }
  }
};
